import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { Error404NotFoundPage } from './error-screens/error404-not-found/error404-not-found.page';
import { EnkashUserAuthGuard } from './error-screens/enkash-user-auth.guard';
import { SelectivePreloadingStrategyService } from './modules/shared/services/selective-preloading-strategy.service';
import { AuthGuardService } from './error-screens/auth-guard.service';
import { RedirectLoginGuard } from './error-screens/redirect-login-guard';
import { environment } from '../environments/environment';

import { WhatsappExpenseReceiptUploadComponent } from './whatsapp-expense-receipt-upload/whatsapp-expense-receipt-upload.component';
import { ExpenseApproverKgComponent } from './expense-approver-kg/expense-approver-kg.component';
import { ExploreProductComponent } from './home/explore-product/explore-product.component';
import { CobrandedTncComponent } from './cobranded-tnc/cobranded-tnc.component';
import { PartnerSsoRedirectComponent } from './partner-sso-redirect/partner-sso-redirect.component';
import { PasswordRequiredGuard } from './error-screens/password-require.guard';
import { VersionComponent } from './version/version.component';
import { CardViewComponent } from './card-view/card-view.component';

const appRoutes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'home',
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
    data: { preload: true },
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'account',
    loadChildren: () => import('./account/account.module').then((m) => m.AccountModule),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'login/old-login',
    loadChildren: () => import('./public/login/login.module').then((m) => m.LoginModule),
    data: { hideHeaderFooter: true },
    canActivate: [AuthGuardService]
  },
  {
    path: 'login',
    loadChildren: () => environment.usePinLogin
      ? import('./modules/auth/pin-login/pin-login.module').then((m) => m.PinLoginModule)
      : import('./public/login/login.module').then((m) => m.LoginModule),
    data: { hideHeaderFooter: true },
    canActivate: [AuthGuardService]
  },
  {
    path: 'signup',
    loadChildren: () => import('./public/signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: 'voucher-details',
    loadChildren: () => import('./voucher-details/voucher-details.module').then((m) => m.VoucherDetailsModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'get-started',
    loadChildren: () => import('./public/get-started/get-started.module').then((m) => m.GetStartedModule),
    data: { hideHeaderFooter: true },
    canActivate: [AuthGuardService]
  },
  {
    path: 'link-gst',
    loadChildren: () => import('./public/onboarding-link-gst/onboarding-link-gst.module').then((m) => m.OnboardingLinkGstModule),
  },
  {
    path: 'kyc',
    loadChildren: () => import('./public/kyc/kyc.module').then((m) => m.KycModule),
  },
  {
    path: 'finbit',
    loadChildren: () => import('./public/link-bank-account/link-bank-account.module').then((m) => m.LinkBankAccountModule),
  },
  {
    path: 'activateCard',
    loadChildren: () => import('./public/activate-gift-card/activate-gift-card.module').then((m) => m.ActivateGiftCardModule),
  },
  {
    path: 'tnc',
    loadChildren: () => import('./tnc/tnc.module').then((m) => m.TncModule),
    data: { hideHeaderFooter: true }
  },
  {
    path: 'partner/tnc',
    component: CobrandedTncComponent,
    data: { hideHeaderFooter: true }
  },
  {
    path: 'card/view',
    component: CardViewComponent,
    data: { hideHeaderFooter: true }
  },
  {
    path: "manage/users",
    loadChildren: () =>
      import("./manage/users/users.module").then((m) => m.UsersModule),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard],
    data: { product: "VS" },
  },
  {
    path: "manage/branches",
    loadChildren: () =>
      import("./manage/branches/branches.module").then((m) => m.BranchesModule),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard],
    data: { product: "VS" },
  },
  {
    path: 'backoffice',
    loadChildren: () => import('./system-admin/system-admin.module').then((m) => m.SystemAdminModule),
    canActivate: [RedirectLoginGuard, EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'approve-request',
    loadChildren: () => import('./approve-request/approve-request.module').then((m) => m.ApproveRequestModule),
    canActivate: [RedirectLoginGuard, EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'bank-cards',
    loadChildren: () => import('./bank-cards/bank-cards.module').then((m) => m.BankCardsModule),
    data: { preload: true },
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'manage/owners',
    loadChildren: () => import('./manage/rent/owners.module').then((m) => m.OwnersModule),
    data: { product: 'AP' },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'backoffice/collections',
    loadChildren: () => import('./admin-config/admin.config.module').then((m) => m.AdminConfigModule),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'marketplace',
    loadChildren: () => import('./marketplace/marketplace.module').then((m) => m.MarketplaceModule),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'channel-incentive-program',
    loadChildren: () =>
      import('./channel-incentive-program/channel-incentive-program.module').then(
        (m) => m.ChannelIncentiveProgramModule
      ),
    canActivate: [EnkashUserAuthGuard, PasswordRequiredGuard]
  },
  {
    path: 'card-performance',
    loadChildren: () => import('./card-performance/card-performance.module').then(m => m.CardPerformanceModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'giftcard',
    loadChildren: () => import('./public/activate-card/activate-card.module').then(m => m.ActivateCardModule),
    data: { hideHeaderFooter: true }
  },
  {
    path: 'credit-cards',
    loadChildren: () => import('./credit-cards/credit-cards.module').then(m => m.CreditCardsModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'partner-dashboard',
    loadChildren: () => import('./partner-dashboard/partner-dashboard.module').then((m) => m.PartnerDashboardModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'pettyCash',
    loadChildren: () => import('./petty-cash-management/pettyCash.module').then((m) => m.PettyCashModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'imprestPayments',
    loadChildren: () => import('./imprest-payments/imprest-payments.module').then((m) => m.ImprestPaymentsModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'employeeBenefits',
    loadChildren: () => import('./employee-benefits/employee-benefits.module').then((m) => m.EmployeeBenefitsModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'erupi',
    loadChildren: () => import('./admin/dashboard-cards/erupi/erupi.module').then((m) => m.ErupiModule),
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'expense-receipt/:uuid',
    component: WhatsappExpenseReceiptUploadComponent
  },
  {
    path: 'expense-approve',
    component: ExpenseApproverKgComponent
  },
  // explore routes
  {
    path: 'explore/payout',
    component: ExploreProductComponent,
    data: {
      explore: 'payout',
      product: 'PAYOUT'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/payables',
    component: ExploreProductComponent,
    data: {
      explore: 'payables',
      product: 'AP'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/channel-incentives',
    component: ExploreProductComponent,
    data: {
      explore: 'channel_incentives',
      product: 'CI'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/vouchers',
    component: ExploreProductComponent,
    data: {
      explore: 'vouchers',
      product: 'VOUCHER'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/expense-management',
    component: ExploreProductComponent,
    data: {
      explore: 'expense_management',
      product: 'EM'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/cards',
    component: ExploreProductComponent,
    data: {
      explore: 'cards',
      product: 'cards'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/employee-rewards',
    component: ExploreProductComponent,
    data: {
      explore: 'employee_rewards',
      product: 'ER'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'explore/verification-suits',
    component: ExploreProductComponent,
    data: {
      explore: 'verification_suits',
      product: 'verification_suits'
    },
    canActivate: [PasswordRequiredGuard]
  },
  {
    path: 'sso/:token',
    component: PartnerSsoRedirectComponent
  },
  {
    path: 'version',
    component: VersionComponent,
    data: { hideHeaderFooter: true },
    canActivate: [RedirectLoginGuard]
  },
  {
    path: 'transaction-logs',
    loadChildren: () =>
      import('./system-admin/payouts/payouts.module').then((m) => m.PayoutsModule),
  },
  // explore routes ending
  {
    path: '',
    loadChildren: () => import('./others.module').then((m) => m.OthersModule),
  },
  {
    path: '**',
    component: Error404NotFoundPage
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: PreloadAllModules,
    })
  ],
  exports: [RouterModule],
  providers: [SelectivePreloadingStrategyService]
})
export class AppRoutingModule { }